// @flow weak
/** @jsx jsx */

import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import createReactClass from 'create-react-class'; // eslint-disable-line no-restricted-syntax
import memoize from 'memoize-weak';
import bemify, { toModifiers } from 'bemify-js';

import { jsx } from '@emotion/core';

import Icon from 'app/common/components/Icon';
import KindPropType from 'app/common/KindPropType';
import Link from 'app/common/components/Link';
import TruncatedText from 'app/common/components/TruncatedText';
import ChoiceList, {
  ChoiceListValuePropType,
} from 'app/common/components/ChoiceList';
import { Header } from 'app/common/components/AccessibleElement';
import { ensureArray } from 'app/common/CommonUtils';
import 'app/common/components/Menu.scss';

const bem = bemify('menu');

// Default component that is rendered within a `Menu`.
// Can be replaced by any other component. This one will
// either be a `Link` or a `span` based on `props.href`.
export const MenuItem = props => {
  const Component = props.href ? Link : 'span';
  const iconBeforeNode =
    typeof props.icon === 'string'
      ? props.icon !== '' && (
          <Icon className={bem('__icon')} type={props.icon} />
        )
      : props.icon;
  const iconAfterNode =
    typeof props.iconAfter === 'string'
      ? props.iconAfter !== '' && (
          <Icon className={bem('__icon --after')} type={props.iconAfter} />
        )
      : props.iconAfter;
  const textNode = (
    <TruncatedText block={true} className={bem('__label-text')}>
      {props.label}
    </TruncatedText>
  );
  const className = bem(
    '__label',
    toModifiers(props.kind, {
      disabled: props.isDisabled,
    }),
    props.className
  );
  const componentProps = !props.href ? {} : { href: props.href };
  return (
    <Component {...componentProps} className={className}>
      <div className={bem('__label-content')}>
        {props.renderContent({
          iconBeforeNode,
          iconAfterNode,
          textNode,
          ...props,
        })}
      </div>
    </Component>
  );
};

MenuItem.propTypes = {
  href: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  iconAfter: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  label: PropTypes.string,
  renderContent: PropTypes.func.isRequired,
  onClick: PropTypes.func,
};

const renderMenuItemContent = ({ textNode, iconBeforeNode, iconAfterNode }) => (
  <React.Fragment>
    {iconBeforeNode}
    {textNode}
    {iconAfterNode}
  </React.Fragment>
);

MenuItem.defaultProps = {
  renderContent: renderMenuItemContent,
};

const itemsPropType = PropTypes.arrayOf(
  PropTypes.shape({
    ..._.omit(MenuItem.propTypes, 'renderContent'), // eslint-disable-line react/forbid-foreign-prop-types
    value: ChoiceListValuePropType,
  })
);

// `ChoiceList` requires that `items` have `value` keys
// and I still think it's good to require those, so add
// them here.
const ensureValue = memoize(item =>
  item.value !== undefined
    ? item
    : {
        ...item,
        value: item.label,
      }
);

const MenuList = props => (
  <ChoiceList
    className={bem('__list', toModifiers(props.kind), props.className)}
    hasCustomChoiceHandlers={props.onChoose === MenuList.defaultProps.onChoose}
    Item={props.MenuItem}
    itemClassName={bem('__item')}
    items={props.items.map(_.ary(ensureValue, 1))}
    onChoose={props.onChoose}
  />
);

MenuList.propTypes = {
  MenuItem: PropTypes.func.isRequired,
  items: itemsPropType.isRequired,
  onChoose: PropTypes.func,
};

MenuList.defaultProps = {
  onChoose: () => {},
};

export const MenuSectionShape = {
  children: (props, propName, componentName) => {
    const children = props[propName];
    return !(children && props.items)
      ? undefined
      : new Error(
          `
      'children' and 'items' supplied to '${componentName}',
      but only one can be supplied and rendered
    `.replace(/\s{2,}/g, ' ')
        );
  },
  headerCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  headerKind: PropTypes.string,
  icon: PropTypes.string,
  isOpen: PropTypes.bool,
  items: itemsPropType,
  kind: KindPropType,
  listClassName: PropTypes.string,
  listStyles: PropTypes.object,
  listKind: PropTypes.string,
  MenuItem: PropTypes.func,
  shouldRenderCount: PropTypes.bool,
  title: PropTypes.string,
  onChoose: PropTypes.func,
  onToggleIsOpen: PropTypes.func,
};

export const MenuSection = props => {
  const hasList = !props.children && !!props.items;
  const canToggle = !!props.onToggleIsOpen;
  const icon = !props.icon ? null : (
    <Icon className={bem('__section-header-icon')} type={props.icon} />
  );
  const toggleIconClassName = bem('__section-header-toggle-icon', {
    '--open': props.isOpen,
  });
  const toggleIcon = !canToggle ? null : (
    <Icon className={toggleIconClassName} type="arrow-right" />
  );
  const headerClassName = bem(
    '__section-header',
    {
      '--interactive': canToggle,
    },
    toModifiers(props.headerKind)
  );
  const hasHeaderCount =
    _.isNumber(props.headerCount) || _.isString(props.headerCount);
  const isCountable = hasHeaderCount || hasList;
  const count = props.shouldRenderCount && isCountable && (
    <div className={bem('__section-header-count')}>
      {hasHeaderCount ? props.headerCount : props.items.length}
    </div>
  );
  const header = !props.title ? null : (
    <Header className={headerClassName} onClick={props.onToggleIsOpen}>
      {icon}
      <TruncatedText className={bem('__section-header-text')}>
        {props.title}
      </TruncatedText>
      {count}
      {toggleIcon}
    </Header>
  );
  const contentClassName = bem('__section-content', {
    '--list': hasList,
  });
  const maybeList =
    !props.items || props.items.length === 0 ? null : (
      <MenuList
        className={props.listClassName}
        css={props.listStyles}
        items={props.items}
        kind={props.listKind}
        MenuItem={props.MenuItem}
        onChoose={props.onChoose}
      />
    );
  const contentChildren = hasList ? maybeList : props.children;
  const content =
    !props.isOpen || !contentChildren ? null : (
      <div className={contentClassName}>{contentChildren}</div>
    );
  return (
    <section
      className={bem('__section', toModifiers(props.kind), props.className)}
    >
      {header}
      {content}
    </section>
  );
};

MenuSection.propTypes = MenuSectionShape;

MenuSection.defaultProps = {
  MenuItem,
  isOpen: true,
  shouldRenderCount: false,
};

export const defaultIsSectionOpen = (id, openIds) => _.includes(openIds, id);

export const MenuSectionsStateful = createReactClass({
  displayName: 'MenuSectionsStateful',

  propTypes: {
    children: PropTypes.any.isRequired,
    canToggleSections: PropTypes.bool.isRequired,
    defaultOpenId: PropTypes.string,
    isSectionOpen: PropTypes.func.isRequired,
    listClassName: MenuSectionShape.listClassName,
    listStyles: MenuSectionShape.listStyles,
    listKind: MenuSectionShape.listKind,
    onChoose: MenuSectionShape.onChoose,
  },

  getDefaultProps() {
    return {
      canToggleSections: true,
      isSectionOpen: defaultIsSectionOpen,
    };
  },

  getInitialState() {
    const { defaultOpenId } = this.props;
    return { openMenuIds: defaultOpenId ? ensureArray(defaultOpenId) : [] };
  },

  onToggleMenu(menuId) {
    this.setState({ openMenuIds: [menuId] });
  },

  render() {
    const { props } = this;
    const renderableChildren = React.Children.toArray(props.children).filter(
      Boolean
    );
    // Prevent toggle controls from rendering when there's only one child
    const hasMultipleChildren = renderableChildren.length > 1;
    const children = renderableChildren.map(child => {
      const id = _.get(child, ['props', 'id']);
      return React.cloneElement(child, {
        isOpen:
          !hasMultipleChildren ||
          props.isSectionOpen(id, this.state.openMenuIds),
        listClassName: props.listClassName,
        listStyles: props.listStyles,
        listKind: props.listKind,
        onChoose: props.onChoose,
        onToggleIsOpen:
          !props.canToggleSections || !hasMultipleChildren
            ? null
            : () => this.onToggleMenu(id),
      });
    });
    return <div className={bem('__sections')}>{children}</div>;
  },
});

export const Menu = ({ className, kind, ...props }) => {
  const content = props.children ? (
    React.Children.map(props.children, child =>
      child && !_.isString(child.type)
        ? React.cloneElement(child, {
            isSectionOpen: props.isSectionOpen,
            listClassName: props.listClassName,
            listStyles: props.listStyles,
            listKind: props.listKind,
            onChoose: props.onChoose,
          })
        : child
    )
  ) : (
    <MenuSection {...props} />
  );
  return <div className={bem(toModifiers(kind), className)}>{content}</div>;
};

// Essentially the same propTypes as `MenuSection`.
// If `children` are passed, they should all be `MenuSection`s.
// This is rather difficult to write a propType checker for.
Menu.propTypes = {
  ...MenuSectionShape,
  isSectionOpen: PropTypes.func,
};

export default Menu;
